// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CalculatedField.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//</copyright>
// ----------------------------------------------------------------------

/**
 * CalculatedField Component
 */
import 'share/utils/Izenda.StringUtilities';
import React, { PropTypes } from 'react';
import ReactDOM from 'react-dom';
import { autorun, observable, toJS } from 'mobx';
import { pure } from 'recompose';
import constants from 'logic/data/Izenda.CalculatedFieldConstants';
import connect from 'share/enhancers/Izenda.Connect';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import CalculatedFieldBody from './Izenda.CalculatedFieldBody';
import { isNil, flatten, remove, isUndefined, isEmpty, find, toLower, get } from 'lodash/fp';

class CalculatedField extends React.Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    type: PropTypes.object.isRequired,
    reportPart: PropTypes.object,
    reportDesigner: PropTypes.object,
    requireLabel: PropTypes.bool,
    isCheckDuplicateWithActualField: PropTypes.bool
  };

  static defaultProps = {
    type: constants.TYPES.ADD,
    requireLabel: true,
    isCheckDuplicateWithActualField: true
  };

  constructor(props: any) {
    super(props);

    this.uiStore = observable({
      showLoading: false
    });

    let { store, value = {}, type } = this.props;

    store.init();
    const model = store.clonedModel;

    model.readInitialData(value, type);

    this.disposeAutoRun = autorun(() => store.getFormatsByType(store.clonedModel.izendaDataType));
  }

  componentWillUnmount() {
    this.disposeAutoRun();
  }

  startLoading = () => {
    this.uiStore.showLoading = true;
  };

  endLoading = () => {
    this.uiStore.showLoading = false;
  };

  /**
   * When click button OK on popup
   */
  onAcceptClosing = () => {
    let { store, localization, validation: valManager, requireLabel, onClose, onAccept } = this.props;

    this.startLoading();
    return this.validateExpression(true)
      .bind(this)
      .then(result => {
        this.endLoading();
        return result;
      })
      .then(isValidExpress => {
        if (!isValidExpress) return;

        return valManager.validate().then(success => {
          const isDuplicate = this.validateDuplicateName();
          const isInvalidName = this.validateName();

          if (isDuplicate) {
            valManager.updateErrors([
              {
                key: constants.VALIDATION_FIELD_NAME,
                messages: String.format(constants.MESSAGES.DUPLICATE_NAME, store.clonedModel.name)
              }
            ]);
            return null;
          }

          if (isInvalidName) {
            valManager.updateErrors([
              {
                key: requireLabel ? constants.VALIDATION_FIELD_NAME : constants.VALIDATION_SUB_TOTAL_FIELD_NAME,
                messages: localization.getText('CALCULATED_FIELD_MESSAGE_INVALID_NAME')
              }
            ]);
            return null;
          }

          if (requireLabel && this.validateIzendaDataType()) {
            valManager.updateErrors([
              {
                key: constants.VALIDATION_DATA_TYPE,
                messages: localization.getText('CALCULATED_FIELD_MESSAGE_MISSING_DATA_TYPE')
              }
            ]);
            return null;
          }

          if (success) {
            valManager.reset();
            store.acceptClonedModel(); // merge final cloned data to real model
            onClose(store.clonedModel);
            onAccept();
          }
          return success;
        });
      });
  };

  validateIzendaDataType(): Boolean {
    const { store: { clonedModel: { izendaDataType } } } = this.props;
    return isNil(izendaDataType);
  }

  /**
   * Validate duplicate name before save
   */
  validateDuplicateName() {
    let { store, store: { clonedModel: model }, isCheckDuplicateWithActualField, type } = this.props;

    if (type.name === constants.TYPES.SUB_TOTAL.name || type.name === constants.TYPES.GRAND_TOTAL.name) {
      return false;
    }

    // Get fieldNames from dataSource first
    let dataSource = store.expressionSelection.expressionFieldName.dataSources;
    let fieldNames = flatten(dataSource.map(source => toJS(source.fieldNames)));

    // check and get fieldName directly from fieldNames
    fieldNames = remove(isUndefined, fieldNames);
    if (isEmpty(fieldNames)) {
      fieldNames = store.expressionSelection.expressionFieldName.fieldNames;
    }

    let duplicatedItem = find(field => {
      return isCheckDuplicateWithActualField
        ? field.isCalculated &&
            field.querySourceId === constants.EMPTY_GUID &&
            toLower(field.name) === toLower(model.name) &&
            (field.tempId ? field.tempId !== model.tempId : field.id !== model.id)
        : toLower(field.name) === toLower(model.name) &&
            (field.id ? field.id !== model.id : field.tempId !== model.tempId);
    }, fieldNames);

    return !!duplicatedItem;
  }

  /**
   * Validate not contain character '[',']' before save
   */
  validateName() {
    const model = this.props.store.clonedModel;

    return model.name && (model.name.indexOf(']') > -1 || model.name.indexOf('[') > -1);
  }

  /**
   * When click button Cancel on popup
   * @param  {Object} hidePopup function
   */
  onCancelClosing = (hidePopup: Function) => {
    this.props.validation.reset();
    hidePopup();
  };

  /**
   * Handle when popup closing
   * @param  {Object} closeCallback function
   * @param  {Object} type of close button click
   */
  onClosing = (closeCallback: Function, type: String) => {
    const hidePopup = () => {
      this.props.onAccept();
      this.props.onClose();
      closeCallback();
    };

    // close expression selection
    this.refs.calculatedFieldContainer.closeExpressionSelection();

    if (type === 'accept') {
      return this.onAcceptClosing();
    } else if (type === 'cancel') {
      return this.onCancelClosing(hidePopup);
    }
  };

  /**
   * validate Expression when focus out expression field
   */
  validateExpression = (noUpdateDataType: Boolean) => {
    let { store, validation: valManager, reportDesigner = {}, reportPart, querySourceId, type } = this.props;
    let previewRecord = get('previewRecord', reportDesigner);
    valManager.reset();

    switch (type.name) {
      case constants.TYPES.SUB_TOTAL.name:
      case constants.TYPES.GRAND_TOTAL.name:
        return store
          .validateExpressionWithReportKey(reportDesigner, reportPart, noUpdateDataType)
          .then(errors => this.updateError(errors));
      default:
        return store
          .validateExpression(querySourceId, noUpdateDataType, previewRecord)
          .then(errors => this.updateError(errors));
    }
  };

  /**
   * Update error to validation context
   */
  updateError = (errors: any) => {
    if (!isEmpty(errors)) {
      this.props.validation.updateErrors(errors);
      return false;
    }

    return true;
  };

  render() {
    let {
      localization,
      type: popupLabels,
      store,
      validation,
      reportDesigner,
      reportPart,
      expressionConfig,
      requireLabel,
      querySourceId
    } = this.props;

    return (
      <Popup
        visible
        onClosing={this.onClosing}
        autoClose={false}
        type="none"
        sizeClass="modal-lg"
        showLoading={this.uiStore.showLoading}
      >
        <Popup.Header title={popupLabels.title} />
        <Popup.Body>
          <CalculatedFieldBody
            ref="calculatedFieldContainer"
            store={store}
            validation={validation}
            type={popupLabels}
            localization={localization}
            reportDesigner={reportDesigner}
            reportPart={reportPart}
            expressionConfig={expressionConfig}
            validateExpression={this.validateExpression}
            startLoading={this.startLoading}
            endLoading={this.endLoading}
            updateError={this.updateError}
            requireLabel={requireLabel}
            querySourceId={querySourceId}
          />
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton key="accept" type="accept">
            {localization.getText('COMMON_LABEL_OK')}
          </Popup.ConfirmationButton>
          <Popup.ConfirmationButton key="cancel" type="cancel">
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

export default connect(pure)(CalculatedField);



// WEBPACK FOOTER //
// ./ui/share/components/controls/calculatedField/Izenda.CalculatedField.js